// Engineering-activity analytics for the WaveHouse stats page. EVERY chart here is
// a cached ClickHouse aggregation exposed as a named pipe (wavehouse/pipes.yaml) and
// read through @wavehouse/sdk as the PUBLIC role — the database analyzing itself,
// live. No raw fetch, no backend of our own: just pipe() calls. Dependency-free
// hand-rolled SVG (no D3) keeps the bundle lean. Bundled into public/app.js via
// dashboard.js, which calls initAnalytics(wh) with the shared SDK client.
// Findings/gaps → the wavehouse MCP tools (AGENTS.md → "Reporting to WaveHouse").
export function initAnalytics(wh) {
// ---- tiny helpers ----------------------------------------------------------
const $ = (id) => document.getElementById(id);
const fmtInt = (n) => Number(n ?? 0).toLocaleString("en-US");
const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const NS = "http://www.w3.org/2000/svg";
function svg(name, attrs = {}, kids = []) {
  const e = document.createElementNS(NS, name);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  for (const c of [].concat(kids)) if (c) e.appendChild(c);
  return e;
}
function frag(name, attrs = {}, kids = []) {
  const e = document.createElement(name);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") e.className = v;
    else if (k === "html") e.innerHTML = v;
    else if (k === "text") e.textContent = v;
    else e.setAttribute(k, v);
  }
  for (const c of [].concat(kids)) if (c) e.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  return e;
}
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

// Brand tokens (mirror of styles.css — SVG fills need literals).
const C = {
  accent: "#06b0bf", accentHi: "#1dc9d8", accentLo: "#054249",
  emerald: "#10b981", amber: "#f59e0b", rose: "#f43f5e",
  ink: "#f1f3f7", muted: "#9ca3af", subtle: "#6b7280",
  grid: "#23272f", surface: "#14171c",
};
// Status palette (reserved; always shown with a legend/label — never color alone).
const CI = { success: C.emerald, failure: C.rose, cancelled: C.amber, skipped: C.subtle };

// ---- shared hover tooltip (mouse hover + touch tap) ------------------------
// Touch is the hard case. A tap synthesizes a `mousemove` (which shows the tip)
// but never a `mouseleave`, and some mobile browsers keep emitting `mousemove`
// afterward — which re-shows the tip and *starves* any timer that resets on show.
// So, in touch mode: (1) the auto-hide is armed ONCE per appearance and never
// reset by later mousemoves; (2) a tap or scroll anywhere that ISN'T a chart mark
// hides the tip and briefly SUPPRESSES re-show (defeats the synthesized-mousemove
// echo); (3) a tap ON a mark still shows it.
let tipEl, tipTimer, touchMode = false, suppressUntil = 0;
const nowMs = () => performance.now();
const onMark = (t) => !!(t && t.closest && t.closest(".vhit, .hm-cell, .hist-bar"));
function tip() {
  if (!tipEl) { tipEl = frag("div", { class: "vtip", hidden: "" }); document.body.appendChild(tipEl); }
  return tipEl;
}
function showTip(html, ev) {
  if (nowMs() < suppressUntil) return; // ignore the post-dismiss mousemove echo
  const t = tip();
  const wasHidden = t.hidden;
  t.innerHTML = html; t.hidden = false;
  const pad = 14, w = t.offsetWidth, h = t.offsetHeight;
  let x = ev.clientX + pad, y = ev.clientY + pad;
  if (x + w > innerWidth - 8) x = ev.clientX - w - pad;
  if (y + h > innerHeight - 8) y = ev.clientY - h - pad;
  t.style.left = `${Math.max(6, x)}px`; t.style.top = `${Math.max(6, y)}px`;
  // Touch backstop: arm the auto-hide only on first appearance so a stream of
  // synthesized mousemoves can't keep pushing it out. Desktop relies on mouseleave.
  if (touchMode && wasHidden) { clearTimeout(tipTimer); tipTimer = setTimeout(hideTip, 2500); }
}
function hideTip() { clearTimeout(tipTimer); if (tipEl) tipEl.hidden = true; }
function dismissTip() { hideTip(); suppressUntil = nowMs() + 600; }
// Global dismissers, registered once. touchstart marks touch mode and dismisses
// unless the tap is on a chart mark (which then shows the tip via its mousemove).
document.addEventListener("touchstart", (e) => { touchMode = true; if (!onMark(e.target)) dismissTip(); }, { capture: true, passive: true });
document.addEventListener("touchmove", dismissTip, { passive: true });
window.addEventListener("scroll", dismissTip, { passive: true });

// ---- data helpers ----------------------------------------------------------
async function pipe(name, params) {
  const r = await wh.pipe(name, params).fetch();
  return r.ok ? (r.data ?? []) : null;
}
const dayKey = (s) => String(s).slice(0, 10);
/** Continuous daily axis: fill gaps in a [day → value] series with zeros so
 *  quiet days read as quiet, not as missing. Returns [{day, ...}] over the span. */
function densify(rows, span) {
  const byDay = new Map(rows.map((r) => [dayKey(r.day), r]));
  const days = [];
  let start;
  if (rows.length) start = new Date(dayKey(rows[0].day) + "T00:00:00Z");
  const end = new Date(); end.setUTCHours(0, 0, 0, 0);
  if (span) { start = new Date(end); start.setUTCDate(start.getUTCDate() - span + 1); }
  if (!start) return [];
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const k = d.toISOString().slice(0, 10);
    days.push({ day: k, row: byDay.get(k) || null });
  }
  return days;
}
const shortDay = (k) => { const [, m, d] = k.split("-"); return `${+m}/${+d}`; };

// ============================================================================
// CHART PRIMITIVES (hand-rolled SVG, hover by default)
// ============================================================================

// Stacked or grouped bar chart over a daily axis. series: [{key,label,color}].
// mode "stack" | "group". Values pulled from row[key] (0 when the day is empty).
function barChart(mount, { axis, series, mode = "stack", height = 150, unit = "" }) {
  mount.textContent = "";
  const W = 720, H = height, padL = 34, padR = 8, padT = 10, padB = 20;
  const iw = W - padL - padR, ih = H - padT - padB;
  const n = axis.length || 1;
  const vals = axis.map((a) => series.reduce((s, se) => s + (mode === "stack" ? Number(a.row?.[se.key] || 0) : 0), 0));
  const groupMax = Math.max(1, ...axis.flatMap((a) => series.map((se) => Number(a.row?.[se.key] || 0))));
  const max = Math.max(1, mode === "stack" ? Math.max(...vals) : groupMax);
  const el = svg("svg", { viewBox: `0 0 ${W} ${H}`, class: "vchart", preserveAspectRatio: "none" });
  const y = (v) => padT + ih - (v / max) * ih;
  // gridlines + y labels (3 ticks)
  for (let t = 0; t <= 2; t++) {
    const v = (max / 2) * t, yy = y(v);
    el.appendChild(svg("line", { x1: padL, y1: yy, x2: W - padR, y2: yy, stroke: C.grid, "stroke-width": 1 }));
    el.appendChild(svg("text", { x: padL - 5, y: yy + 3, "text-anchor": "end", class: "vaxis" }, [txt(Math.round(v))]));
  }
  const bw = iw / n;
  axis.forEach((a, i) => {
    const x0 = padL + i * bw;
    const cells = [];
    if (mode === "stack") {
      let acc = 0;
      for (const se of series) {
        const v = Number(a.row?.[se.key] || 0); if (!v) continue;
        const h = (v / max) * ih;
        cells.push({ se, v, y: padT + ih - acc - h, h, x: x0 + bw * 0.18, w: bw * 0.64 });
        acc += h;
      }
    } else {
      const gw = (bw * 0.7) / series.length;
      series.forEach((se, j) => {
        const v = Number(a.row?.[se.key] || 0); if (!v) return;
        const h = (v / max) * ih;
        cells.push({ se, v, y: padT + ih - h, h, x: x0 + bw * 0.15 + j * gw, w: gw * 0.86 });
      });
    }
    for (const c of cells) {
      const r = svg("rect", { x: c.x.toFixed(1), y: c.y.toFixed(1), width: Math.max(0.5, c.w).toFixed(1), height: Math.max(0.5, c.h).toFixed(1), rx: 1.5, fill: c.se.color, class: "vbar" });
      el.appendChild(r);
    }
    // hover hit-area over the whole day column
    const hit = svg("rect", { x: x0, y: padT, width: bw, height: ih, fill: "transparent", class: "vhit" });
    hit.addEventListener("mousemove", (ev) => {
      const rows = series.map((se) => { const v = Number(a.row?.[se.key] || 0); return v ? `<span class="tk" style="background:${se.color}"></span>${esc(se.label)} <b>${fmtInt(v)}</b>` : ""; }).filter(Boolean);
      showTip(`<div class="tt-h">${esc(shortDay(a.day))}</div>${rows.join("<br>") || `<span class="muted">no activity</span>`}`, ev);
    });
    hit.addEventListener("mouseleave", hideTip);
    el.appendChild(hit);
    // sparse x labels
    if (n <= 16 || i % Math.ceil(n / 12) === 0) el.appendChild(svg("text", { x: x0 + bw / 2, y: H - 6, "text-anchor": "middle", class: "vaxis" }, [txt(shortDay(a.day))]));
  });
  mount.appendChild(el);
  if (unit) mount.appendChild(frag("div", { class: "vunit muted", text: unit }));
}
const txt = (s) => document.createTextNode(String(s));

// Horizon band: a compressed area chart. The value range is split into `bands`
// slices; each slice is scaled to full height and layered with rising accent
// intensity — high days glow bright, quiet days stay dim. points: number[].
function horizonRow(mount, { name, sum, sub, strong, points, max, bands = 3 }) {
  const W = 720, H = 34, n = points.length || 1;
  // 3-col grid: label | plot | number — so a long name (e.g. "Issues opened")
  // never wraps into the number, and the number stays right-aligned + centered.
  const row = frag("div", { class: "hz-row" });
  row.appendChild(frag("div", { class: "hz-label" + (strong ? " strong" : ""), text: name ?? "", title: name ?? "" }));
  const box = frag("div", { class: "hz-plot" });
  const el = svg("svg", { viewBox: `0 0 ${W} ${H}`, class: "hz", preserveAspectRatio: "none" });
  el.appendChild(svg("rect", { x: 0, y: 0, width: W, height: H, fill: "rgba(6,176,191,0.05)" }));
  const m = Math.max(1, max || Math.max(...points));
  const band = m / bands;
  const x = (i) => (n === 1 ? W / 2 : (i * W) / (n - 1));
  for (let b = 0; b < bands; b++) {
    let d = `M0 ${H}`;
    points.forEach((v, i) => {
      const slice = clamp(v - b * band, 0, band) / band; // 0..1 within this band
      d += ` L${x(i).toFixed(1)} ${(H - slice * H).toFixed(1)}`;
    });
    d += ` L${W} ${H} Z`;
    el.appendChild(svg("path", { d, fill: C.accent, "fill-opacity": (0.28 + 0.34 * b).toFixed(2), stroke: "none" }));
  }
  // hover
  const hit = svg("rect", { x: 0, y: 0, width: W, height: H, fill: "transparent", class: "vhit" });
  hit.addEventListener("mousemove", (ev) => {
    const box2 = el.getBoundingClientRect();
    const i = clamp(Math.round(((ev.clientX - box2.left) / box2.width) * (n - 1)), 0, n - 1);
    const d = mount._days?.[i];
    showTip(`<div class="tt-h">${esc(sub ? sub + " · " : "")}${d ? esc(shortDay(d)) : ""}</div><b>${fmtInt(points[i])}</b> events`, ev);
  });
  hit.addEventListener("mouseleave", hideTip);
  el.appendChild(hit);
  box.appendChild(el);
  row.appendChild(box);
  row.appendChild(frag("div", { class: "hz-sum", text: sum ?? "" }));
  mount.appendChild(row);
}

// 7×24 punch-card heatmap (sequential accent). cells: [{dow,hour,n}].
function heatmap(mount, cells) {
  mount.textContent = "";
  const dows = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const grid = new Map(cells.map((c) => [`${c.dow}-${c.hour}`, Number(c.n)]));
  const max = Math.max(1, ...cells.map((c) => Number(c.n)));
  const wrap = frag("div", { class: "hm" });
  // hour header
  const head = frag("div", { class: "hm-row hm-head" });
  head.appendChild(frag("div", { class: "hm-day" }));
  for (let h = 0; h < 24; h++) head.appendChild(frag("div", { class: "hm-cell hm-hh", text: h % 6 === 0 ? String(h) : "" }));
  wrap.appendChild(head);
  for (let d = 1; d <= 7; d++) {
    const r = frag("div", { class: "hm-row" });
    r.appendChild(frag("div", { class: "hm-day", text: dows[d - 1] }));
    for (let h = 0; h < 24; h++) {
      const v = grid.get(`${d}-${h}`) || 0;
      const inten = v ? 0.14 + 0.86 * Math.sqrt(v / max) : 0;
      const cell = frag("div", { class: "hm-cell" });
      cell.style.background = v ? `rgba(6,176,191,${inten.toFixed(3)})` : "rgba(255,255,255,0.02)";
      cell.addEventListener("mousemove", (ev) => showTip(`<div class="tt-h">${dows[d - 1]} ${h}:00 UTC</div><b>${fmtInt(v)}</b> events`, ev));
      cell.addEventListener("mouseleave", hideTip);
      r.appendChild(cell);
    }
    wrap.appendChild(r);
  }
  mount.appendChild(wrap);
}

// Simple single-series histogram (ordered buckets). buckets: [{bucket,n}].
function histogram(mount, buckets) {
  mount.textContent = "";
  if (!buckets.length) { mount.appendChild(frag("div", { class: "empty", text: "No merged PRs in range." })); return; }
  const max = Math.max(1, ...buckets.map((b) => Number(b.n)));
  const wrap = frag("div", { class: "hist" });
  for (const b of buckets) {
    const v = Number(b.n);
    const col = frag("div", { class: "hist-col" });
    const bar = frag("div", { class: "hist-bar" });
    bar.style.height = `${clamp((v / max) * 100, v ? 4 : 0, 100)}%`;
    bar.addEventListener("mousemove", (ev) => showTip(`<div class="tt-h">${esc(b.bucket)}</div><b>${fmtInt(v)}</b> PRs`, ev));
    bar.addEventListener("mouseleave", hideTip);
    col.appendChild(frag("div", { class: "hist-n muted", text: v || "" }));
    col.appendChild(bar);
    col.appendChild(frag("div", { class: "hist-x", text: b.bucket }));
    wrap.appendChild(col);
  }
  mount.appendChild(wrap);
}

// ============================================================================
// SECTIONS
// ============================================================================
let DAYS = 30;

function setStat(id, v, suffix = "") {
  const e = $(id); if (!e) return;
  // Treat null/empty AND NaN (failed/empty pipe → Number(undefined)) as "—", never "NaN".
  const bad = v == null || v === "" || (typeof v === "number" && !Number.isFinite(v));
  e.textContent = bad ? "—" : (typeof v === "number" ? fmtInt(v) : v) + suffix;
}

async function loadSummary() {
  const d = (await pipe("gh_overview"))?.[0];
  if (!d) return;
  setStat("s-contributors", Number(d.contributors));
  setStat("s-pushes", Number(d.pushes));
  setStat("s-prs", Number(d.prs_opened));
  setStat("s-ci", Number(d.ci_runs));
  setStat("s-ci-pct", Number(d.ci_success_pct), "%");
  setStat("s-ships", Number(d.ships));
  setStat("s-open-prs", Number(d.open_prs));
  setStat("s-open-issues", Number(d.open_issues));
}

const CATS = [
  { key: "pushes", label: "Pushes" }, { key: "prs_opened", label: "PRs opened" },
  { key: "prs_closed", label: "PRs closed" }, { key: "issues_opened", label: "Issues opened" },
  { key: "issues_closed", label: "Issues closed" }, { key: "reviews", label: "Reviews" },
  { key: "comments", label: "Comments" },
];
async function loadActivityByType() {
  const mount = $("activity-horizons"); mount.textContent = "";
  const rows = await pipe("gh_activity_by_type", { days: DAYS });
  if (!rows) return mount.appendChild(frag("div", { class: "empty", text: "Activity unavailable." }));
  // pivot to day → {category:n}
  const byDay = {};
  for (const r of rows) { (byDay[dayKey(r.day)] ||= {})[r.category] = Number(r.n); }
  const axis = densify(Object.keys(byDay).map((day) => ({ day })).sort((a, b) => a.day < b.day ? -1 : 1), DAYS <= 90 ? DAYS : null);
  const days = axis.map((a) => a.day);
  const globalMax = Math.max(1, ...days.flatMap((d) => CATS.map((c) => byDay[d]?.[c.key] || 0)));
  mount._days = days;
  for (const cat of CATS) {
    const points = days.map((d) => byDay[d]?.[cat.key] || 0);
    if (points.every((p) => !p)) continue;
    const total = points.reduce((a, b) => a + b, 0);
    horizonRow(mount, { name: cat.label, sum: fmtInt(total), sub: cat.label, points, max: globalMax });
  }
  mount._days = days;
}

async function loadContributors() {
  const board = $("contrib-board"), horizons = $("contrib-horizons");
  board.textContent = ""; horizons.textContent = "";
  const [people, activity] = await Promise.all([
    pipe("gh_contributors", { days: DAYS, limit: 12 }),
    pipe("gh_contributor_activity", { days: DAYS, limit: 8 }),
  ]);
  if (!people) { board.appendChild(frag("div", { class: "empty", text: "Contributors unavailable." })); return; }
  const maxC = Math.max(1, ...people.map((p) => Number(p.contributions)));
  for (const p of people) {
    const parts = [["pushes", p.pushes, C.accent], ["prs", p.prs, C.emerald], ["reviews", p.reviews, C.accentHi], ["issues", p.issues, C.amber], ["comments", p.comments, C.subtle]];
    const bar = frag("div", { class: "cbar" });
    for (const [, v, col] of parts) { if (!Number(v)) continue; const seg = frag("div"); seg.style.flex = String(Number(v)); seg.style.background = col; bar.appendChild(seg); }
    const card = frag("div", { class: "ccard" }, [
      frag("a", { class: "cav", href: esc(p.profile_url || "#"), rel: "noreferrer", html: p.avatar_url ? `<img src="${esc(p.avatar_url)}" width="34" height="34" alt="" loading="lazy" referrerpolicy="no-referrer">` : "" }),
      frag("div", { class: "cbody" }, [
        frag("div", { class: "crow" }, [
          frag("a", { class: "cname", href: esc(p.profile_url || "#"), rel: "noreferrer", text: p.actor_login }),
          frag("span", { class: "cscore", text: `${fmtInt(p.contributions)}` }),
        ]),
        bar,
        frag("div", { class: "cmeta muted", text: `${fmtInt(p.pushes)} pushes · ${fmtInt(p.prs)} PRs · ${fmtInt(p.reviews)} reviews · ${fmtInt(p.issues)} issues · ${fmtInt(p.comments)} comments` }),
      ]),
    ]);
    board.appendChild(card);
  }
  // per-contributor horizon small-multiples
  if (activity?.length) {
    const byActor = {};
    for (const r of activity) { (byActor[r.actor_login] ||= {})[dayKey(r.day)] = Number(r.n); }
    const axis = densify(activity.map((r) => ({ day: r.day })).sort((a, b) => a.day < b.day ? -1 : 1), DAYS <= 90 ? DAYS : null);
    const days = axis.map((a) => a.day); horizons._days = days;
    const order = people.map((p) => p.actor_login).filter((l) => byActor[l]);
    const gmax = Math.max(1, ...order.flatMap((l) => days.map((d) => byActor[l]?.[d] || 0)));
    for (const login of order.slice(0, 8)) {
      const points = days.map((d) => byActor[login]?.[d] || 0);
      horizonRow(horizons, { name: login, strong: true, sub: login, points, max: gmax });
    }
    horizons._days = days;
  }
}

async function loadPRs() {
  const [flow, stats, buckets] = await Promise.all([
    pipe("gh_pr_flow", { days: DAYS }), pipe("gh_pr_stats", { days: DAYS }), pipe("gh_pr_merge_buckets", { days: Math.max(DAYS, 90) }),
  ]);
  const s = stats?.[0] || {};
  setStat("pr-opened", Number(s.prs_opened));
  setStat("pr-merged", Number(s.prs_merged));
  setStat("pr-rate", s.merge_rate_pct == null ? "—" : Number(s.merge_rate_pct), s.merge_rate_pct == null ? "" : "%");
  setStat("pr-ttm", fmtHours(s.median_hours_to_merge));
  setStat("pr-ttfr", fmtHours(s.median_hours_to_first_review));
  const mount = $("pr-flow");
  if (flow) barChart(mount, {
    axis: densify(flow.slice().sort((a, b) => a.day < b.day ? -1 : 1), DAYS <= 90 ? DAYS : null),
    series: [{ key: "merged", label: "Merged", color: C.emerald }, { key: "closed_unmerged", label: "Closed", color: C.subtle }, { key: "opened", label: "Opened", color: C.accent }],
    mode: "group", height: 160,
  });
  histogram($("pr-hist"), (buckets || []));
}
function fmtHours(h) {
  if (h == null || Number.isNaN(Number(h)) || String(h) === "nan") return "—";
  h = Number(h);
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h % 1 ? h.toFixed(1) : h}h`;
  return `${(h / 24).toFixed(1)}d`;
}

async function loadIssues() {
  const [flow, stats] = await Promise.all([pipe("gh_issue_flow", { days: DAYS }), pipe("gh_issue_stats", { days: DAYS })]);
  const s = stats?.[0] || {};
  setStat("iss-opened", Number(s.opened));
  setStat("iss-closed", Number(s.closed));
  setStat("iss-ttc", fmtHours(s.median_hours_to_close));
  setStat("iss-open", Number(s.open_now));
  if (flow) barChart($("iss-flow"), {
    axis: densify(flow.slice().sort((a, b) => a.day < b.day ? -1 : 1), DAYS <= 90 ? DAYS : null),
    series: [{ key: "opened", label: "Opened", color: C.amber }, { key: "closed", label: "Closed", color: C.emerald }],
    mode: "group", height: 150,
  });
}

async function loadCI() {
  const [health, byWf] = await Promise.all([pipe("gh_ci_health", { days: DAYS }), pipe("gh_ci_by_workflow", { days: DAYS, limit: 12 })]);
  if (health) barChart($("ci-flow"), {
    axis: densify(health.slice().sort((a, b) => a.day < b.day ? -1 : 1), DAYS <= 90 ? DAYS : null),
    series: [{ key: "success", label: "Success", color: CI.success }, { key: "failure", label: "Failure", color: CI.failure }, { key: "cancelled", label: "Cancelled", color: CI.cancelled }, { key: "skipped", label: "Skipped", color: CI.skipped }],
    mode: "stack", height: 160,
  });
  const t = $("ci-workflows"); t.textContent = "";
  if (byWf?.length) {
    for (const w of byWf) {
      const pct = Number(w.success_pct);
      const tone = pct >= 90 ? C.emerald : pct >= 70 ? C.amber : C.rose;
      t.appendChild(frag("div", { class: "wf-row" }, [
        frag("div", { class: "wf-name", text: w.workflow, title: w.workflow }),
        frag("div", { class: "wf-meter" }, [(() => { const f = frag("div", { class: "wf-fill" }); f.style.width = `${pct}%`; f.style.background = tone; return f; })()]),
        frag("div", { class: "wf-pct", text: `${pct}%` }),
        frag("div", { class: "wf-runs muted", text: `${fmtInt(w.runs)} runs` }),
      ]));
    }
  } else t.appendChild(frag("div", { class: "empty", text: "No CI runs in range." }));
}

async function loadHeatmap() {
  const cells = await pipe("gh_activity_heatmap", { days: Math.max(DAYS, 90) });
  if (cells) heatmap($("heatmap"), cells);
}

async function loadShips() {
  const rows = await pipe("gh_ship_cadence", { days: DAYS });
  if (rows) barChart($("ships"), {
    axis: densify(rows.slice().sort((a, b) => a.day < b.day ? -1 : 1), DAYS <= 90 ? DAYS : null),
    series: [{ key: "ships", label: "Publishes", color: C.accentHi }], mode: "stack", height: 140,
  });
}

async function loadReviews() {
  const edges = await pipe("gh_review_network", { days: Math.max(DAYS, 90), limit: 20 });
  const m = $("reviews"); m.textContent = "";
  if (!edges?.length) { m.appendChild(frag("div", { class: "empty", text: "No cross-member reviews in range." })); return; }
  const max = Math.max(1, ...edges.map((e) => Number(e.reviews)));
  for (const e of edges) {
    const f = frag("div", { class: "rv-fill" }); f.style.width = `${(Number(e.reviews) / max) * 100}%`;
    m.appendChild(frag("div", { class: "rv-row" }, [
      frag("div", { class: "rv-pair", html: `<b>${esc(e.reviewer)}</b> <span class="muted">reviewed</span> <b>${esc(e.author)}</b>` }),
      frag("div", { class: "rv-meter" }, [f]),
      frag("div", { class: "rv-n muted", text: fmtInt(e.reviews) }),
    ]));
  }
}

// ---- range selector + boot -------------------------------------------------
function reloadWindowed() {
  loadActivityByType(); loadContributors(); loadPRs(); loadIssues(); loadCI(); loadHeatmap(); loadShips(); loadReviews();
}
function initRange() {
  const bar = $("range");
  bar.querySelectorAll("button").forEach((b) => b.addEventListener("click", () => {
    bar.querySelectorAll("button").forEach((x) => x.classList.remove("on"));
    b.classList.add("on"); DAYS = Number(b.dataset.days); reloadWindowed();
  }));
}

initRange();
loadSummary();
reloadWindowed();
}
